import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { send, notify } from '../../../services/NotificationService';
import { deleteToolPhoto, upsertToolAsset } from '../../../data/loca/tools';

// Photos privees de l'outil : jamais publiees (lokapp.fr, page de location), elles servent a envoyer
// plus de photos a un client qui en demande, notamment via les agents IA.
const MAX_PHOTO_SIDE = 2000;
const PHOTO_QUALITY = 0.85;

const stripExtension = (fileName) => (fileName || '').replace(/\.[^.]+$/, '');

const toJpegFileName = (fileName) => `${stripExtension(fileName) || 'photo'}.jpg`;

const downloadPhoto = (photo) => {
    const link = document.createElement('a');
    link.href = photo.fileData;
    link.download = photo.fileName || toJpegFileName(photo.title);
    link.click();
};

// Redimensionne (cote max 2000 px) et convertit en JPEG pour garder des enregistrements legers.
const readPhoto = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
        const ratio = Math.min(1, MAX_PHOTO_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.naturalWidth * ratio);
        canvas.height = Math.round(img.naturalHeight * ratio);
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL('image/jpeg', PHOTO_QUALITY));
    };
    img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error(`Impossible de lire l'image ${file.name}`));
    };
    img.src = url;
});

function PhotoCard({ tool, photo, onChanged, onPreview }) {
    const [title, setTitle] = useState(photo.title || '');
    const [description, setDescription] = useState(photo.description || '');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setTitle(photo.title || '');
        setDescription(photo.description || '');
    }, [photo.id, photo.title, photo.description]);

    const dirty = title !== (photo.title || '') || description !== (photo.description || '');

    const save = async () => {
        setBusy(true);
        try {
            const response = await upsertToolAsset(tool.publicId, {
                id: photo.id,
                assetType: 'photo',
                title,
                description,
                fileName: '',
                fileData: '',
                youtubeUrl: ''
            });
            if (response?.id) {
                send('success', 'Photo modifiée');
                notify();
                await onChanged();
            }
        } catch (error) {
            send('warning', error.message || 'Impossible de modifier la photo');
            notify();
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        if (!window.confirm(`Supprimer la photo « ${photo.title || photo.fileName || 'Photo'} » ?`)) return;
        setBusy(true);
        try {
            const response = await deleteToolPhoto(tool.publicId, photo.id);
            if (response?.status === 200) {
                send('success', 'Photo supprimée');
                notify();
                await onChanged();
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <figure className="tool-photo-card" data-asset-id={photo.id}>
            <button type="button" className="tool-photo-card__preview" onClick={onPreview} title="Voir en grand">
                <img src={photo.fileData} alt={photo.description || photo.title || 'Photo'} loading="lazy" />
            </button>
            <figcaption>
                <InputText value={title} disabled={busy} onChange={(e) => setTitle(e.target.value)} placeholder="Titre" title={title} maxLength={200} />
                <InputText value={description} disabled={busy} onChange={(e) => setDescription(e.target.value)} placeholder="Ce que montre la photo" title={description} maxLength={500} />
                {dirty && <Button size="small" disabled={busy} onClick={save}>Enregistrer</Button>}
                <div className="tool-photo-card__actions">
                    <Button size="small" severity="secondary" onClick={() => downloadPhoto(photo)}>Télécharger</Button>
                    <Button size="small" severity="danger" disabled={busy} onClick={remove}>Supprimer</Button>
                </div>
            </figcaption>
        </figure>
    );
}

export default function ToolPhotoAssets({ tool, photos, onChanged }) {
    const [uploading, setUploading] = useState(null);
    const [previewIndex, setPreviewIndex] = useState(null);
    const preview = previewIndex !== null ? photos[previewIndex] : null;

    const showSibling = (step) => setPreviewIndex((index) => (index + step + photos.length) % photos.length);

    // Fleches gauche / droite pour passer d'une photo a l'autre dans l'apercu.
    useEffect(() => {
        if (previewIndex === null || photos.length < 2) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'ArrowLeft') showSibling(-1);
            if (event.key === 'ArrowRight') showSibling(1);
        };
        // Phase de capture : le Dialog PrimeReact stoppe la propagation des touches.
        window.addEventListener('keydown', onKeyDown, true);
        return () => window.removeEventListener('keydown', onKeyDown, true);
    }, [previewIndex, photos.length]);

    useEffect(() => {
        if (previewIndex !== null && previewIndex >= photos.length) setPreviewIndex(null);
    }, [previewIndex, photos.length]);

    const uploadPhotos = async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = '';
        if (!files.length) return;

        let added = 0;
        try {
            for (const [index, file] of files.entries()) {
                setUploading({ current: index + 1, total: files.length });
                const fileData = await readPhoto(file);
                const response = await upsertToolAsset(tool.publicId, {
                    assetType: 'photo',
                    title: stripExtension(file.name),
                    description: '',
                    fileName: toJpegFileName(file.name),
                    fileData,
                    youtubeUrl: ''
                });
                if (response?.id) added++;
            }
        } catch (error) {
            send('warning', error.message || "Impossible d'enregistrer la photo");
        } finally {
            setUploading(null);
            if (added > 0) {
                send('success', added > 1 ? `${added} photos ajoutées` : 'Photo ajoutée');
                await onChanged();
            }
            notify();
        }
    };

    const downloadAll = () => {
        photos.forEach((photo, index) => setTimeout(() => downloadPhoto(photo), index * 300));
    };

    return (
        <section className="tool-assets-box tool-photos-box">
            <div className="tool-photos-box__header">
                <div>
                    <h3>Photos privées ({photos.length})</h3>
                    <p className="tool-photos-box__hint">
                        Jamais publiées sur le site public ni sur la page de location : elles servent à envoyer
                        plus de photos à un client qui en demande (agents IA compris). Décrivez ce que montre
                        chaque photo pour retrouver la bonne.
                    </p>
                </div>
                {photos.length > 1 && (
                    <Button size="small" severity="secondary" onClick={downloadAll}>Tout télécharger</Button>
                )}
            </div>

            <label className="tool-photos-box__upload">
                <input type="file" accept="image/*" multiple disabled={!!uploading} onChange={uploadPhotos} />
            </label>
            {uploading && <p>Enregistrement de la photo {uploading.current} / {uploading.total}...</p>}

            {photos.length === 0 ? (
                <p>Aucune photo pour le moment.</p>
            ) : (
                <div className="tool-photos-grid">
                    {photos.map((photo, index) => (
                        <PhotoCard key={photo.id} tool={tool} photo={photo} onChanged={onChanged} onPreview={() => setPreviewIndex(index)} />
                    ))}
                </div>
            )}

            <Dialog
                visible={!!preview}
                onHide={() => setPreviewIndex(null)}
                header={preview ? `${preview.title || 'Photo'}${photos.length > 1 ? ` (${previewIndex + 1} / ${photos.length})` : ''}` : ''}
                className="tool-photo-preview"
                dismissableMask
                draggable={false}
                resizable={false}
            >
                {preview && (
                    <>
                        <div className="tool-photo-preview__stage">
                            {photos.length > 1 && (
                                <Button icon="pi pi-chevron-left" rounded text aria-label="Photo précédente" className="tool-photo-preview__nav tool-photo-preview__nav--prev" onClick={() => showSibling(-1)} />
                            )}
                            <img src={preview.fileData} alt={preview.description || preview.title || 'Photo'} />
                            {photos.length > 1 && (
                                <Button icon="pi pi-chevron-right" rounded text aria-label="Photo suivante" className="tool-photo-preview__nav tool-photo-preview__nav--next" onClick={() => showSibling(1)} />
                            )}
                        </div>
                        <div className="tool-photo-preview__footer">
                            <span>{preview.description}</span>
                            <Button size="small" severity="secondary" onClick={() => downloadPhoto(preview)}>Télécharger</Button>
                        </div>
                    </>
                )}
            </Dialog>
        </section>
    );
}
